import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { normalizeSkills } from "@/utils/skillNormalization";

export const useSkillsFilter = () => {
  const [skills, setSkills] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const loadSkills = useCallback(async () => {
    if (hasFetchedRef.current) return;
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: candidatesData, error: fetchError } = await supabase
          .from('candidates')
          .select('skills')
          .not('skills', 'is', null);

        if (fetchError) throw fetchError;

        const allSkillsArray: string[] = [];
        candidatesData.forEach(candidate => {
          if (candidate.skills) {
            candidate.skills.forEach((skill: string) => {
              if (skill?.trim())
                allSkillsArray.push(skill.trim());
            });
          }
        });

        const normalized = normalizeSkills(allSkillsArray);
        setSkills(normalized);
        hasFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching skills:', err);
        setError(err instanceof Error ? err.message : 'Failed to load skills');
      } finally {
        setIsLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    return run;
  }, []);

  return { skills, isLoading, error, loadSkills };
};
